import {Link} from 'react-router';

import {productT} from '@/lib/types/product.types';
import {SelectedColumnsT} from '@/lib/types/query.types';

import {renderProductTableStats} from '@/lib/config/productTableConfig';
import {createDataTableActions, createDataTableColumns, createDataTableConfig} from '@/lib/helpers/config';

import {useProductData} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';

import {ConfirmDeleteModalComponent, ModalFormComponent, StatsRowComponent} from '@/components';
import {DataTable} from '@/components/dataTable';
import {DataTableRpc} from '@/components/dataTableRpc';

import {useBrand} from '../providers/brandDetailsProvider';

/**
 * Brand Detail Products Component
 * @param {brandT} brand - The brand object
 * @returns {JSX.Element}
 */
export const BrandDetailProducts = () => {
  const {isAdmin} = useAuth();
  const {brand} = useBrand();

  const {
    formData,
    setFormData,
    isVisible,
    isEditing,
    setIsVisible,
    isDeleteModalVisible,
    setIsDeleteModalVisible,
    isSubmitting,
    submissionError,
    createFields,
    handleAddProductClick,
    handleEditProductClick,
    handleDeleteProductClick,
    handleSubmitProduct,
    handleUpdateProduct,
    handleDeleteProduct,
    stats,
  } = useProductData(brand?.brand_id);

  // Data Table -----------------------------------------------------
  const {table, title, columns, filters, searchColumn} = createDataTableConfig({
    table: 'Product',
    title: 'Product Summary',
    searchColumn: 'product_name',
    columns: [
      'product_id',
      'product_name',
      'product_is_active',
      'product_category',
      'product_subcategory',
      'product_strain',
      'product_ancestor_category',
      'product_rec_price',
      'product_image_url',
      ['Brand', ['brand_id', 'brand_name']],
    ],
    filters: [
      {
        column: 'fk_brand_id',
        operator: 'eq',
        value: brand?.brand_id,
      },
    ],
  });

  // Helper types to make the code cleaner and DRY
  type DataTableT = typeof table;
  type DataTableColumnsT = typeof columns;
  type DataTableRowT = SelectedColumnsT<DataTableT, DataTableColumnsT>;

  const statsRow = renderProductTableStats(stats?.total, stats?.active, stats?.inactive);

  const columnsDisplay = createDataTableColumns<DataTableT, DataTableRowT>([
    {
      header: 'Avatar',
      field: 'product_image_url',
      render: product => (
        <img
          className="h-10 w-10 rounded-sm"
          src={product.product_image_url || '/images/logo/logo.png'}
          alt={product.product_name || ''}
        />
      ),
    },
    {
      header: 'Name',
      field: 'product_name',
      render: product => (
        <Link to={`/products/${product.product_id}`} className="text-primary hover:text-primary dark:text-primary">
          {product.product_name}
        </Link>
      ),
    },
    {
      header: 'Category',
      field: 'product_category',
    },
    {
      header: 'Sub Category',
      field: 'product_subcategory',
    },
    {
      header: 'Ancestor Category',
      field: 'product_ancestor_category',
    },
  ]);

  const actionsDisplay = createDataTableActions<DataTableT, DataTableRowT>([
    {
      icon: 'ri-pencil-line',
      color: 'info',
      handler: row => handleEditProductClick(row.product_id),
      permission: () => isAdmin,
    },
    {
      icon: 'ri-delete-bin-line',
      color: 'danger',
      handler: row => handleDeleteProductClick(row.product_id),
      permission: () => isAdmin,
    },
  ]);

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable
        table={table}
        title={title}
        columns={columns}
        columnsDisplay={columnsDisplay}
        actionsDisplay={actionsDisplay}
        onAdd={handleAddProductClick}
        filters={filters}
        searchColumn={searchColumn}
      />
      <DataTableRpc
        functionName="get_brand_product_summary"
        functionArgs={{brand_id_input: brand?.brand_id}}
        columns={[
          {
            header: 'Name',
            field: 'product_name',
          },
          {
            header: 'Category',
            field: 'product_category',
          },
          {
            header: 'Sub Category',
            field: 'product_subcategory',
          },
          {
            header: 'Sub Category',
            field: 'product_subcategory',
          },
          {
            header: 'Ancestor Category',
            field: 'product_ancestor_category',
          },
          {
            header: 'Retail Price',
            field: 'retail_price_low',
            render: row => `$${row.retail_price_low} - $${row.retail_price_high}`,
          },
          {
            header: 'Position Count',
            field: 'position_count',
          },
        ]}
        enabled={!!brand?.brand_id}
      />
      <ModalFormComponent
        isVisible={isVisible && !isDeleteModalVisible}
        fields={createFields}
        formData={formData}
        onSubmit={(data: productT) => {
          if (isEditing) {
            handleUpdateProduct(data);
          } else {
            handleSubmitProduct({...data, fk_brand_id: brand?.brand_id});
          }
        }}
        setFormData={setFormData}
        onClose={() => setIsVisible(false)}
        error={submissionError}
        isSubmitting={isSubmitting}
        title={isEditing ? 'Edit Product' : 'Create New Product'}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDeleteProduct}
        title="Confirm Deletion"
        description="Are you sure you want to delete this product? This action cannot be undone."
        isSubmitting={isSubmitting}
      />
    </>
  );
};
